import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import Skeleton from './Skeleton';

const Table = ({ data = [], columns = [], options = {}, loading = false }) => {
  const defaultOptions = {
    pagination: true,
    pageSize: 10,
    pageSizeOptions: [5, 10, 20, 50],
    showSearch: true,
    searchPlaceholder: 'Buscar...',
    emptyMessage: 'No hay datos disponibles',
    className: '',
    rowClassName: '',
    headerClassName: '',
    sortable: true,
  };

  const tableOptions = { ...defaultOptions, ...options };

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(tableOptions.pageSize);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'none' });
  const [animKey, setAnimKey] = useState(0);
  const prevSearchRef = useRef('');
  const prevPageRef = useRef(1);

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    return Object.values(item).some(value =>
      value !== null && value !== undefined &&
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const sortedData = React.useMemo(() => {
    let items = [...filteredData];
    if (sortConfig.key && sortConfig.direction !== 'none') {
      items.sort((a, b) => {
        const av = a[sortConfig.key], bv = b[sortConfig.key];
        if (av == null) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (bv == null) return sortConfig.direction === 'ascending' ? 1 : -1;
        if (typeof av === 'number' && typeof bv === 'number')
          return sortConfig.direction === 'ascending' ? av - bv : bv - av;
        return sortConfig.direction === 'ascending'
          ? av.toString().localeCompare(bv.toString())
          : bv.toString().localeCompare(av.toString());
      });
    }
    return items;
  }, [filteredData, sortConfig]);

  const paginatedData = React.useMemo(() => {
    if (!tableOptions.pagination) return sortedData;
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, tableOptions.pagination]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  useEffect(() => {
    setCurrentPage(1);
    if (prevSearchRef.current !== searchTerm) {
      setAnimKey(k => k + 1);
      prevSearchRef.current = searchTerm;
    }
  }, [data, searchTerm]);

  useEffect(() => {
    if (prevPageRef.current !== currentPage) {
      setAnimKey(k => k + 1);
      prevPageRef.current = currentPage;
    }
  }, [currentPage]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleSort = (key) => {
    if (!tableOptions.sortable) return;
    let direction = 'ascending';
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'ascending') direction = 'descending';
      else if (sortConfig.direction === 'descending') direction = 'none';
    }
    setSortConfig({ key, direction });
    setAnimKey(k => k + 1);
  };

  const renderSortIcon = (columnKey) => {
    if (!tableOptions.sortable) return null;
    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'ascending')
        return <FaSortUp className="ml-1 inline" style={{ color: '#DC1F26' }} />;
      if (sortConfig.direction === 'descending')
        return <FaSortDown className="ml-1 inline" style={{ color: '#DC1F26' }} />;
    }
    return <FaSort className="ml-1 inline" style={{ color: '#3A3D52' }} />;
  };

  return (
    <div className="w-full">
      {/* Top bar */}
      <div className="flex flex-col md:flex-row justify-between mb-5 gap-3">
        {tableOptions.showSearch && (
          <input
            type="text"
            placeholder={tableOptions.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-72 h-9 text-sm focus:outline-none"
            style={{
              backgroundColor: '#1a1b30',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '10px',
              padding: '0 12px',
              color: '#E8EAF0',
              caretColor: '#00C2FF',
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            }}
            onFocus={e => {
              e.target.style.borderColor = 'rgba(0,194,255,0.4)';
              e.target.style.boxShadow = '0 0 0 2px rgba(0,194,255,0.08)';
            }}
            onBlur={e => {
              e.target.style.borderColor = 'rgba(255,255,255,0.08)';
              e.target.style.boxShadow = 'none';
            }}
          />
        )}
        {tableOptions.pagination && (
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: '#4A5168' }}>Filas:</span>
            <select
              className="text-xs rounded-md px-2 py-1 focus:outline-none"
              style={{
                backgroundColor: '#1a1b30',
                border: '1px solid rgba(255,255,255,0.07)',
                color: '#8B92A8',
              }}
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            >
              {tableOptions.pageSizeOptions.map(s => (
                <option key={s} value={s} style={{ backgroundColor: '#1e1e26' }}>{s}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
        <table className={`min-w-full ${tableOptions.className}`} style={{ backgroundColor: '#0a0b16' }}>
          <thead style={{ backgroundColor: '#12132a', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`px-4 py-3 text-left text-xs font-semibold select-none ${column.headerClassName || ''}`}
                  style={{ color: '#6B7391', ...column.headerStyle }}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                >
                  <div className="flex items-center cursor-pointer gap-1">
                    {column.title}
                    {column.sortable !== false && renderSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 0 }}>
                  <Skeleton.TableRows rows={tableOptions.pageSize > 10 ? 8 : Math.min(tableOptions.pageSize, 8)} cols={columns.length} />
                </td>
              </tr>
            ) : paginatedData.length > 0 ? (
              <AnimatePresence mode="sync">
                {paginatedData.map((row, rowIndex) => (
                  <motion.tr
                    key={`${animKey}-${rowIndex}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.35,
                      ease: [0.16, 1, 0.3, 1],
                      delay: Math.min(rowIndex, 12) * 0.03,
                    }}
                    className={`${tableOptions.rowClassName}`}
                    style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.025)';
                      e.currentTarget.style.boxShadow = 'inset 2px 0 0 rgba(0,194,255,0.3)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className={`px-4 py-3 text-sm ${column.className || ''}`}
                        style={{ color: '#C8CDD9', ...column.style }}
                      >
                        {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key]}
                      </td>
                    ))}
                  </motion.tr>
                ))}
              </AnimatePresence>
            ) : (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-sm" style={{ color: '#3A3D52' }}>
                  {tableOptions.emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {tableOptions.pagination && totalPages > 0 && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-xs" style={{ color: '#4A5168' }}>
            {paginatedData.length} de {sortedData.length} resultados
          </div>
          <div className="flex items-center gap-1">
            <button
              className="h-8 w-8 flex items-center justify-center rounded-lg transition-all duration-150"
              style={{
                backgroundColor: '#1a1b30',
                border: '1px solid rgba(255,255,255,0.07)',
                color: '#6B7391',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: currentPage === 1 ? 0.35 : 1,
              }}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <FaChevronLeft size={11} />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;

              const active = currentPage === pageNum;
              return (
                <motion.button
                  key={i}
                  whileTap={{ scale: 0.92 }}
                  className="h-8 w-8 flex items-center justify-center rounded-lg text-xs font-medium transition-all duration-150"
                  style={active ? {
                    background: 'linear-gradient(135deg, #DC1F26, #B8161D)',
                    color: '#fff',
                    boxShadow: '0 0 12px rgba(220,31,38,0.4)',
                    border: 'none',
                  } : {
                    backgroundColor: '#1a1b30',
                    border: '1px solid rgba(255,255,255,0.07)',
                    color: '#6B7391',
                  }}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </motion.button>
              );
            })}

            <button
              className="h-8 w-8 flex items-center justify-center rounded-lg transition-all duration-150"
              style={{
                backgroundColor: '#1a1b30',
                border: '1px solid rgba(255,255,255,0.07)',
                color: '#6B7391',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                opacity: currentPage === totalPages ? 0.35 : 1,
              }}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <FaChevronRight size={11} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
